<template>
  <v-container fluid>
    <v-row :dense="small" align="center" justify="center" class="text-center">
      <v-col cols="10"> <!-- name -->
        <v-row align="center" class="text-center" justify="center">
        <v-btn v-if="trackIsPinned" icon class="d-inline-block" :small="small" @click="clearPinned" color="orange"
          ><v-icon>mdi-close-circle-outline</v-icon></v-btn
        >
        <v-btn v-else icon @click="pin" :small="small" color="orange"
          ><v-icon>mdi-pin</v-icon></v-btn
        >
        <div class="d-inline-block ml-2">
          <span v-if="small" :class="textSize" v-line-clamp:20="1" class="d-inline-block">{{ track.name }}</span>
          <span v-else :class="textSize" class="d-inline-block">{{ track.name }}</span>
          <v-btn icon :x-small="small" @click="openNotes(track)" class="ml-2"><v-icon>mdi-note</v-icon></v-btn>
          <v-btn icon v-if="allowEdit" @click="edit(track)"
            ><v-icon>mdi-pencil</v-icon></v-btn
          >
          <v-btn icon v-if="allowEdit" @click="openDeleteDialog(track)"
            ><v-icon>mdi-delete</v-icon></v-btn
          >
        </div>
        </v-row>
      </v-col>
    </v-row>

    <v-row align="center" class="my-0 py-0 text-center">
      <v-col cols="12" class="my-0 py-0">
        <v-btn icon :x-small="small" @click="removeTick"><v-icon>mdi-minus</v-icon></v-btn>
        <svg v-for="box in boxes" :key="box" :width="boxHeight" :height="boxHeight" style="margin: 1px;">
          <!-- outline -->
          <rect
            :height="boxHeight"
            :width="boxHeight"
            stroke-width="2"
            :stroke="strokeColor"
            fill-opacity="0"
          />
          <template v-if="box < fillingIndex">
            <!-- fully-filled -->
            <line
              x1="0"
              y1="0"
              :x2="boxHeight"
              :y2="boxHeight"
              stroke-width="1"
              :stroke="strokeColor"
            />

            <!-- top-right to bottom-left -->
            <line
              :x1="boxHeight"
              y1="0"
              x2="0"
              :y2="boxHeight"
              stroke-width="1"
              :stroke="strokeColor"
            />

            <!-- horizontal -->
            <line
              x1="0"
              :y1="halfBoxHeight"
              :x2="boxHeight"
              :y2="halfBoxHeight"
              stroke-width="1"
              :stroke="strokeColor"
            />

            <!-- vertical -->
            <line
              :x1="halfBoxHeight"
              y1="0"
              :x2="halfBoxHeight"
              :y2="boxHeight"
              stroke-width="1"
              :stroke="strokeColor"
            />
          </template>

          <template v-if="box == fillingIndex">
            <template v-if="leftoverTicks < 1"></template>

            <template v-else-if="leftoverTicks == 1">
              <!-- top-left to bottom-right -->
              <line
                x1="0"
                y1="0"
                :x2="boxHeight"
                :y2="boxHeight"
                stroke-width="1"
                :stroke="strokeColor"
              />
            </template>

            <template v-else-if="leftoverTicks == 2">
              <!-- top-left to bottom-right -->
              <line
                x1="0"
                y1="0"
                :x2="boxHeight"
                :y2="boxHeight"
                stroke-width="1"
                :stroke="strokeColor"
              />
              <!-- top-right to bottom-left -->
              <line
                :x1="boxHeight"
                y1="0"
                x2="0"
                :y2="boxHeight"
                stroke-width="1"
                :stroke="strokeColor"
              />
            </template>

            <template v-else-if="leftoverTicks == 3">
              <!-- top-left to bottom-right -->
              <line
                x1="0"
                y1="0"
                :x2="boxHeight"
                :y2="boxHeight"
                stroke-width="1"
                :stroke="strokeColor"
              />
              <!-- top-right to bottom-left -->
              <line
                :x1="boxHeight"
                y1="0"
                x2="0"
                :y2="boxHeight"
                stroke-width="1"
                :stroke="strokeColor"
              />
              <!-- horizontal -->
              <line
                x1="0"
                :y1="halfBoxHeight"
                :x2="boxHeight"
                :y2="halfBoxHeight"
                stroke-width="1"
                :stroke="strokeColor"
              />
            </template>
          </template>
        </svg>
      <v-btn icon :x-small="small" @click="addTick"><v-icon>mdi-plus</v-icon></v-btn>
        </v-col>
    </v-row>

    <v-row justify="center" class="my-0 py-0">
      <span class="overline">({{ track.rank.title }})</span>
    </v-row>

    <ProgressEditDialog v-model="editDialog" :track="editedTrack" />
    <ProgressDeleteDialog v-model="deleteDialog" :track="deletingTrack" />
    <ProgressNotesDialog v-model="viewNotesDialog" :track="notesTrack" />
  </v-container>
</template>

<script>
  import {mapGetters} from 'vuex';
  import ProgressEditDialog from '@/components/ProgressEditDialog';
  import ProgressDeleteDialog from '@/components/ProgressDeleteDialog';
  import ProgressNotesDialog from '@/components/ProgressNotesDialog';

  export default {
    props: {
      track: Object,
      allowEdit: {
        type: Boolean,
        default: false,
      },
      onDark: {
        type: Boolean,
        default: false,
      },
      small: {
        type: Boolean,
        default: false,
      },
    },
    components: {ProgressEditDialog, ProgressDeleteDialog, ProgressNotesDialog},
    computed: {
      ...mapGetters('progress', ['pinnedProgressTrack']),
      leftoverTicks() {
        return this.track.ticks % 4;
      },
      totalProgressFilled() {
        return Math.floor(this.track.ticks / 4);
      },
      fillingIndex() {
        return this.totalProgressFilled;
      },
      trackIsPinned() {
        if (this.pinnedProgressTrack) {
          return this.track.uuid === this.pinnedProgressTrack.uuid;
        }
        return false;
      },
      textSize() {
        if (this.small) {
          return 'caption';
        }
        return 'subtitle-1';
      },
      strokeColor() {
        if (this.onDark) {
          return 'white';
        }
        return 'black';
      },
      boxHeight() {
        if (this.small) {
          return 18;
        }
        return 25;
      },
      halfBoxHeight() {
        return Math.floor(this.boxHeight / 2);
      },
    },
    data() {
      return {
        boxes: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9],
        editedTrack: {},
        editDialog: false,
        deletingTrack: {},
        deleteDialog: false,
        notesTrack: {},
        viewNotesDialog: false,
      };
    },
    methods: {
      edit(track) {
        this.editDialog = true;
        this.editedTrack = track;
      },
      openDeleteDialog(track) {
        this.deleteDialog = true;
        this.deletingTrack = track;
      },
      addTick() {
        this.$store.dispatch('progress/addTick', this.track);
      },
      removeTick() {
        this.$store.dispatch('progress/removeTick', this.track);
      },
      pin() {
        this.$store.dispatch('progress/pinProgressTrack', {
          uuid: this.track.uuid,
        });
      },
      clearPinned() {
        this.$store.dispatch('progress/clearPinnedProgressTrack');
      },
      openNotes(track) {
        this.viewNotesDialog = true;
        this.notesTrack = track;
      },
    },
  };
</script>
